// Financial Reporting tab — DSCR-Watch state and its toggle.
//
// The watch is the PRECONDITION for the Monthly DSCR-Watch request type, so the
// panel lives with that type rather than floating above every type as loan-wide
// furniture: a reader on Quarterly has no use for it, and a reader on
// DSCR-Watch needs it beside the thing it gates.
//
// 🔴 The missed-month fact used to live here and now does not. It is the point
// of the whole feature — requests are issued MANUALLY each month (decision 10),
// so a month nobody got round to looks exactly like a month nobody has reached
// yet, and `currentMonthIssued` comes back from the server precisely so the tab
// can say it out loud. Saying it HERE meant only a reader who had already
// chosen the DSCR-Watch type ever saw it, which is backwards. It is now stated
// twice, in the two places it can be acted on: as an always-visible chip in the
// attention strip above the type filter, and beside this type's issue action in
// its header. Neither is a copy of this panel; this panel no longer says it.

import { useState } from "react";
import {
  Button,
  Card,
  StatusBadge,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatDateInTz } from "@/design-system/lib/format";
import type { DscrWatchStateDto } from "@/api/portfolioAdmin/reportRequestsApi";
import { useSetDscrWatch } from "@/hooks/portfolioAdmin/useReportRequests";

interface DscrWatchPanelProps {
  loanId: string;
  watch: DscrWatchStateDto;
  /** Whether the viewer may turn the watch on/off (Compliance.RecordFinancialOpsCheck). */
  canToggle: boolean;
  /** Viewer's IANA zone, for the genuine instants on this panel. */
  tz?: string | null;
}

/** The id the type header's "Put on watch" door focuses. */
export const DSCR_WATCH_TOGGLE_ID = "dscr-watch-toggle";

export function DscrWatchPanel({ loanId, watch, canToggle, tz }: DscrWatchPanelProps) {
  const toast = useToast();
  const setWatch = useSetDscrWatch(loanId);
  const [modalOpen, setModalOpen] = useState(false);
  const [note, setNote] = useState("");

  const turningOff = watch.isOnDscrWatch;

  const submit = () => {
    setWatch.mutate(
      { on: !turningOff, note: note.trim() || null },
      {
        onSuccess: () => {
          setModalOpen(false);
          setNote("");
          toast.success(turningOff ? "DSCR-Watch cleared." : "Loan put on DSCR-Watch.");
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Card data-testid="dscr-watch-panel">
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="flex items-center gap-space-2">
            <h3 className="text-body font-semibold text-onyx m-0">DSCR-Watch</h3>
            <StatusBadge tone={watch.isOnDscrWatch ? "warn" : "neutral"}>
              {watch.isOnDscrWatch ? "On watch" : "Off"}
            </StatusBadge>
          </div>
          <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-prose">
            {watch.isOnDscrWatch ? (
              <>
                On watch since {watch.startedAt ? formatDateInTz(watch.startedAt, tz) : "—"}
                {watch.startedByUserName ? ` · ${watch.startedByUserName}` : ""}. While a
                loan is on watch, a Monthly DSCR-Watch request is issued by hand each
                month — there is no schedule doing it for you.
              </>
            ) : (
              <>
                Put a loan on watch to collect monthly DSCR figures. Requests are still
                issued by hand each month; the watch is what tells you a month is owed.
              </>
            )}
          </p>
        </div>

        {canToggle && (
          <Button
            id={DSCR_WATCH_TOGGLE_ID}
            variant={watch.isOnDscrWatch ? "secondary" : "primary"}
            size="sm"
            className="shrink-0"
            onClick={() => {
              setNote("");
              setModalOpen(true);
            }}
            data-testid="dscr-watch-toggle"
          >
            {watch.isOnDscrWatch ? "Clear watch" : "Put on watch"}
          </Button>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={turningOff ? "Clear DSCR-Watch" : "Put loan on DSCR-Watch"}
        description={
          turningOff
            ? "Monthly DSCR-Watch requests will stop being owed for this loan."
            : "A Monthly DSCR-Watch request becomes owed each month. You issue each one by hand from this tab."
        }
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={setWatch.isPending}
            >
              Cancel
            </Button>
            <Button
              variant={turningOff ? "destructive" : "primary"}
              loading={setWatch.isPending}
              onClick={submit}
              data-testid="dscr-watch-confirm"
            >
              {turningOff ? "Clear watch" : "Put on watch"}
            </Button>
          </>
        }
      >
        <label
          className="block text-small font-medium text-onyx mb-space-1"
          htmlFor="dscr-watch-note"
        >
          Note <span className="text-gray-mid font-normal">(optional)</span>
        </label>
        <Textarea
          id="dscr-watch-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Why this loan is (or is no longer) on DSCR-Watch…"
        />
      </Modal>
    </Card>
  );
}
